/**
 * Shared Types + Constants for /try Client.
 *
 * WHAT THIS FILE DOES:
 * - Defines data contracts shared across auth, client API, and UI modules.
 * - Centralizes constants for polling, protocol headers, and media typing.
 * - Provides a single WebTryError type and error message mapping.
 * - Exposes Firebase config shape injected by SSR.
 *
 * WHY THIS EXISTS:
 * - The /try bundle is split into focused modules (auth, client, UI).
 * - Shared primitives prevent import cycles and reduce duplication.
 * - Keeps each module under the "no god module" size guideline.
 *
 * USED BY:
 * - src/client/try-auth.ts (auth + bridge)
 * - src/client/try-client.ts (API wrapper + polling)
 * - src/client/try-ui.ts (DOM controller + messaging)
 * - src/client/try.ts (entry point wiring)
 *
 * ARCHITECTURE CONTEXT:
 * - /try is a single-input web ingest surface.
 * - Auth can come from Firebase or the extension bridge.
 * - Client modules need a shared vocabulary for statuses + payloads.
 *
 * GLOBAL CONTRACTS:
 * - window.__GIST_TRY_CONFIG__ contains Firebase client config or null.
 * - window.__GIST_TRY_MEDIA_CONFIG__ contains /try media ingest config.
 * - window.gistTry is the client API instance (for debugging/console use).
 *
 * ERROR HANDLING:
 * - WebTryError is the single error type thrown by client helpers.
 * - resolveTryErrorMessage() maps stable codes to user-readable text.
 *
 * DESIGN PRINCIPLES:
 * - Keep data shapes explicit (no implicit "any").
 * - Prefer stable error codes for UI mapping.
 * - Keep constants small and self-describing.
 *
 * NON-GOALS:
 * - UI rendering logic (lives in try-ui.ts).
 * - Network calls (lives in try-client.ts + try-utils.ts).
 * - Auth side effects (lives in try-auth.ts).
 *
 * @file src/client/try-shared.ts
 */

import type { User } from 'firebase/auth';

/**
 * Firebase config shape injected by SSR.
 */
export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
}

/**
 * Locked output contract for /try client audio transcoding.
 *
 * Phase 0 decision:
 * - /try client-side media transcode output is always MP3 (`audio/mpeg`)
 * - bitrate is fixed at 32 kbps for speech-heavy summarization quality/size tradeoff
 * - mono output keeps payloads small without harming speech understanding
 */
export const TryClientAudioTranscodeFormat = Object.freeze({
  OUTPUT_MIME_TYPE: 'audio/mpeg',
  OUTPUT_BITRATE_KBPS: 32,
  OUTPUT_CHANNEL_COUNT: 1,
  OUTPUT_SAMPLE_RATE_HZ: 16_000
} as const);

/**
 * Global ingest limits shared by /try UI and media pipeline modules.
 */
export const TryMediaIngestLimits = Object.freeze({
  /**
   * Gemini practical audio limit for 1M-token context, represented as duration.
   */
  MAX_MEDIA_DURATION_MS: 9.5 * 60 * 60 * 1000,

  /**
   * Maximum file size accepted for any upload (1.5 GB).
   * Matches backend DEFAULT_MAX_INPUT_BYTES in media-upload.ts.
   * Prevents users from waiting through a long upload only to get a server rejection.
   */
  MAX_FILE_SIZE_BYTES: 1.5 * 1024 * 1024 * 1024,

  /**
   * Maximum document file size for Docling uploads (30 MiB).
   * Matches TryDocumentFileSupport.MAX_FILE_BYTES and Docling's upload cap.
   */
  MAX_DOCUMENT_SIZE_BYTES: 30 * 1024 * 1024
} as const);

/**
 * Default runtime controls for /try client audio transcoding.
 *
 * These defaults keep the feature disabled until rollout and provide explicit
 * timeout/size guardrails for deterministic fallback behavior.
 */
export const TryClientAudioTranscodeDefaults = Object.freeze({
  ENABLED: false,
  TIMEOUT_MS: 120_000,
  MAX_INPUT_BYTES: 1_500 * 1024 * 1024,
  MAX_OUTPUT_BYTES: 256 * 1024 * 1024
} as const);

/**
 * Safety bounds for coercing SSR-injected transcode config.
 */
const TryClientAudioTranscodeBounds = Object.freeze({
  MIN_TIMEOUT_MS: 5_000,
  MAX_TIMEOUT_MS: 20 * 60 * 1000,
  MIN_MAX_INPUT_BYTES: 5 * 1024 * 1024,
  MAX_MAX_INPUT_BYTES: 10 * 1024 * 1024 * 1024,
  MIN_MAX_OUTPUT_BYTES: 1 * 1024 * 1024,
  MAX_MAX_OUTPUT_BYTES: 512 * 1024 * 1024
} as const);

/**
 * Runtime configuration for the /try client-side transcode path.
 */
export interface TryClientAudioTranscodeConfig {
  enabled: boolean;
  outputMimeType: typeof TryClientAudioTranscodeFormat.OUTPUT_MIME_TYPE;
  outputBitrateKbps: typeof TryClientAudioTranscodeFormat.OUTPUT_BITRATE_KBPS;
  outputChannelCount: typeof TryClientAudioTranscodeFormat.OUTPUT_CHANNEL_COUNT;
  outputSampleRateHz: typeof TryClientAudioTranscodeFormat.OUTPUT_SAMPLE_RATE_HZ;
  timeoutMs: number;
  maxInputBytes: number;
  maxOutputBytes: number;
}

/**
 * Boot payload injected by /try SSR.
 */
export interface TryMediaBootstrapConfig {
  clientAudioTranscode: TryClientAudioTranscodeConfig;
}

/**
 * Stable fallback reasons for /try client media transcoding.
 */
export const TryMediaTranscodeFallbackReasons = Object.freeze({
  DISABLED: 'disabled',
  UNSUPPORTED_API: 'unsupported_api',
  UNSUPPORTED_MEDIA_TYPE: 'unsupported_media_type',
  INPUT_TOO_LARGE: 'input_too_large',
  DURATION_TOO_LONG: 'duration_too_long',
  OUTPUT_TOO_LARGE: 'output_too_large',
  UNSUPPORTED_CODEC: 'unsupported_codec',
  DEMUX_FAILED: 'demux_failed',
  DECODE_FAILED: 'decode_failed',
  ENCODE_FAILED: 'encode_failed',
  TIMEOUT: 'timeout',
  ABORTED: 'aborted',
  UNKNOWN: 'unknown'
} as const);

export type TryMediaTranscodeFallbackReason =
  typeof TryMediaTranscodeFallbackReasons[keyof typeof TryMediaTranscodeFallbackReasons];

/**
 * Coarse media kind labels used for /try telemetry dimensions.
 */
export const TryMediaKinds = Object.freeze({
  AUDIO: 'audio',
  VIDEO: 'video',
  UNKNOWN: 'unknown'
} as const);

export type TryMediaKind = typeof TryMediaKinds[keyof typeof TryMediaKinds];

/**
 * Byte + routing telemetry snapshot emitted by media client-first routing.
 *
 * Produced by:
 * - src/client/try-client.ts initAndUploadMediaClientFirst()
 *
 * Consumed by:
 * - src/client/try-ui.ts for submit-path telemetry emission
 * - src/client/try-speculation.ts for speculative media telemetry emission
 */
export interface TryMediaClientTranscodeTelemetry {
  usedClientTranscode: boolean;
  fallbackReason: TryMediaTranscodeFallbackReason | null;
  mediaKind: TryMediaKind;
  originalContentType: string | null;
  uploadedContentType: string | null;
  originalBytes: number;
  uploadedBytes: number;
  bytesSaved: number;
  reductionRatio: number;
}

/**
 * Minimal analytics event envelope for /try client telemetry.
 *
 * This mirrors POST /api/analytics/events event objects.
 */
export interface TryAnalyticsEventInput {
  event: string;
  timestamp?: string;
  authState?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Upload progress contract shared by /try upload callers.
 */
export interface TryFileUploadProgress {
  loadedBytes: number;
  totalBytes: number | null;
  percent: number | null;
}

/**
 * UI-facing phases for /try media ingest orchestration.
 */
export const TryMediaIngestPhases = Object.freeze({
  EXTRACTING_AUDIO: 'extracting_audio',
  COMPRESSING_AUDIO: 'compressing_audio',
  UPLOADING: 'uploading',
  FALLING_BACK: 'falling_back'
} as const);

export type TryMediaIngestPhase =
  typeof TryMediaIngestPhases[keyof typeof TryMediaIngestPhases];

/**
 * Event payload emitted during /try media ingest.
 */
export interface TryMediaIngestPhaseEvent {
  phase: TryMediaIngestPhase;
  fallbackReason: TryMediaTranscodeFallbackReason | null;
}

declare global {
  interface Window {
    __GIST_TRY_CONFIG__?: FirebaseConfig | null;
    __GIST_TRY_MEDIA_CONFIG__?: TryMediaBootstrapConfig | null;
    __GIST_LANDING_USER__?: {
      uid: string;
      paidEntitlement: boolean;
    } | null;
    gistTry?: unknown;
  }
}

/**
 * Resolves a normalized /try media bootstrap config from unknown input.
 *
 * Called by:
 * - src/ssr/try.ts when serializing SSR boot payload
 * - client modules when reading window.__GIST_TRY_MEDIA_CONFIG__
 */
export function resolveTryMediaBootstrapConfig(raw: unknown): TryMediaBootstrapConfig {
  const objectValue = asRecord(raw);
  const rawTranscode = asRecord(objectValue?.clientAudioTranscode);

  return {
    clientAudioTranscode: {
      enabled: resolveBooleanValue(rawTranscode?.enabled, TryClientAudioTranscodeDefaults.ENABLED),
      outputMimeType: TryClientAudioTranscodeFormat.OUTPUT_MIME_TYPE,
      outputBitrateKbps: TryClientAudioTranscodeFormat.OUTPUT_BITRATE_KBPS,
      outputChannelCount: TryClientAudioTranscodeFormat.OUTPUT_CHANNEL_COUNT,
      outputSampleRateHz: TryClientAudioTranscodeFormat.OUTPUT_SAMPLE_RATE_HZ,
      timeoutMs: resolveBoundedInteger(rawTranscode?.timeoutMs, {
        defaultValue: TryClientAudioTranscodeDefaults.TIMEOUT_MS,
        min: TryClientAudioTranscodeBounds.MIN_TIMEOUT_MS,
        max: TryClientAudioTranscodeBounds.MAX_TIMEOUT_MS
      }),
      maxInputBytes: resolveBoundedInteger(rawTranscode?.maxInputBytes, {
        defaultValue: TryClientAudioTranscodeDefaults.MAX_INPUT_BYTES,
        min: TryClientAudioTranscodeBounds.MIN_MAX_INPUT_BYTES,
        max: TryClientAudioTranscodeBounds.MAX_MAX_INPUT_BYTES
      }),
      maxOutputBytes: resolveBoundedInteger(rawTranscode?.maxOutputBytes, {
        defaultValue: TryClientAudioTranscodeDefaults.MAX_OUTPUT_BYTES,
        min: TryClientAudioTranscodeBounds.MIN_MAX_OUTPUT_BYTES,
        max: TryClientAudioTranscodeBounds.MAX_MAX_OUTPUT_BYTES
      })
    }
  };
}

/**
 * Converts unknown values into an object dictionary.
 */
function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  return value as Record<string, unknown>;
}

/**
 * Resolves boolean-like values from string/boolean input.
 */
function resolveBooleanValue(value: unknown, defaultValue: boolean): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value !== 'string') {
    return defaultValue;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === '1' || normalized === 'true') {
    return true;
  }
  if (normalized === '0' || normalized === 'false') {
    return false;
  }
  return defaultValue;
}

/**
 * Resolves integer config values with min/max bounds.
 */
function resolveBoundedInteger(
  value: unknown,
  options: { defaultValue: number; min: number; max: number }
): number {
  let parsed = Number.NaN;
  if (typeof value === 'number') {
    parsed = value;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^-?\d+$/.test(trimmed)) {
      parsed = Number.parseInt(trimmed, 10);
    }
  }

  if (!Number.isFinite(parsed)) {
    return options.defaultValue;
  }

  const integerValue = Math.trunc(parsed);
  if (integerValue < options.min) {
    return options.min;
  }
  if (integerValue > options.max) {
    return options.max;
  }
  return integerValue;
}

/**
 * Maps transcode errors to stable fallback reason codes.
 *
 * Called by:
 * - src/client/try-media-transcode.ts when deciding server fallback path reason.
 */
export function resolveMediaTranscodeFallbackReason(error: unknown): TryMediaTranscodeFallbackReason {
  if (error instanceof WebTryError) {
    switch (error.message) {
      case 'media_transcode_disabled':
        return TryMediaTranscodeFallbackReasons.DISABLED;
      case 'media_transcode_unsupported_api':
        return TryMediaTranscodeFallbackReasons.UNSUPPORTED_API;
      case 'media_transcode_unsupported_media_type':
      case 'invalid_media_type':
        return TryMediaTranscodeFallbackReasons.UNSUPPORTED_MEDIA_TYPE;
      case 'media_transcode_input_too_large':
        return TryMediaTranscodeFallbackReasons.INPUT_TOO_LARGE;
      case 'media_duration_too_long':
        return TryMediaTranscodeFallbackReasons.DURATION_TOO_LONG;
      case 'media_transcode_output_too_large':
        return TryMediaTranscodeFallbackReasons.OUTPUT_TOO_LARGE;
      case 'media_transcode_unsupported_codec':
        return TryMediaTranscodeFallbackReasons.UNSUPPORTED_CODEC;
      case 'media_transcode_demux_failed':
        return TryMediaTranscodeFallbackReasons.DEMUX_FAILED;
      case 'media_transcode_decode_failed':
        return TryMediaTranscodeFallbackReasons.DECODE_FAILED;
      case 'media_transcode_encode_failed':
        return TryMediaTranscodeFallbackReasons.ENCODE_FAILED;
      case 'media_transcode_timeout':
        return TryMediaTranscodeFallbackReasons.TIMEOUT;
      case 'media_transcode_aborted':
        return TryMediaTranscodeFallbackReasons.ABORTED;
      default:
        return TryMediaTranscodeFallbackReasons.UNKNOWN;
    }
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return TryMediaTranscodeFallbackReasons.ABORTED;
  }

  return TryMediaTranscodeFallbackReasons.UNKNOWN;
}

/**
 * Polling timing constants used by the /try client.
 */
export const PollingConfig = {
  /** Poll interval for media extraction status (fast feedback for uploads). */
  MEDIA_STATUS_INTERVAL_MS: 2_000,
  /** Maximum wait for media extraction before failing. */
  MEDIA_STATUS_TIMEOUT_MS: 10 * 60 * 1000,
  /**
   * Grace period where a just-created uploadId may briefly return 404/missing.
   *
   * Why this exists:
   * - The backend persists init state in KV.
   * - Even when the init endpoint awaits the KV put, edge/KV propagation can
   *   still produce very short "missing" windows for fast clients.
   * - Treating early missing as pending avoids flaking on good connections.
   */
  MEDIA_STATUS_MISSING_GRACE_MS: 2_000,
  /** Poll interval for content readiness via /api/extension/check. */
  CONTENT_STATUS_INTERVAL_MS: 5_000,
  /** Maximum wait for summary readiness before failing. */
  CONTENT_STATUS_TIMEOUT_MS: 20 * 60 * 1000
} as const;

/**
 * Protocol headers required by the backend API version gate.
 */
export const ProtocolHeaders = {
  CLIENT_VERSION: 'web-try',
  PROTOCOL_VERSION: '2'
} as const;

/**
 * Supported media file extensions (used when File.type is missing).
 */
export const MediaContentTypeByExtension: Record<string, string> = {
  mp3: 'audio/mpeg',
  mp4: 'video/mp4',
  m4a: 'audio/mp4',
  wav: 'audio/wav',
  webm: 'video/webm',
  mov: 'video/quicktime',
  ogg: 'audio/ogg'
} as const;

/**
 * Error class for web ingest failures.
 */
export class WebTryError extends Error {
  public readonly status: number | null;
  public readonly details: unknown;

  constructor(message: string, options: { status?: number | null; details?: unknown } = {}) {
    super(message, { cause: options?.details });
    this.name = 'WebTryError';
    this.status = typeof options.status === 'number' ? options.status : null;
    this.details = options.details ?? null;
  }
}

/**
 * Minimal snapshot of current auth state.
 */
export interface AuthSnapshot {
  mode: 'extension' | 'firebase' | 'none';
  workerToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  paidEntitlement: boolean | null;
}

/**
 * Media upload status response shape.
 */
export interface MediaUploadStatus {
  status: 'pending' | 'ready' | 'error' | 'missing';
  audioFileUri?: string | null;
  durationMs?: number | null;
  error?: string;
}

/**
 * Response shape for ingest endpoints.
 */
export interface IngestResponse {
  contentId?: string;
  wsUrl?: string | null;
  wsToken?: string | null;
  status?: string;
  storyStream?: unknown[] | null;
  /**
   * Server-side share URL (try-speed-plan Phase 2a).
   * When present, the client skips the separate POST /api/share call.
   */
  publicUrl?: string;
}

/**
 * Response shape for share creation.
 */
export interface ShareResponse {
  publicUrl?: string;
  id?: string;
  domainKey?: string;
  language?: string;
  isRenderable?: boolean;
  summaryVersion?: 'instant' | null;
  storyStreamR2Key?: string | null;
}

/**
 * Maps internal error codes to user-friendly messages.
 */
export function resolveTryErrorMessage(error: unknown): string {
  const resolveMediaTooLargeMessage = (details: unknown): string => {
    if (!details || typeof details !== 'object') {
      return 'Media file exceeds the upload size limit.';
    }
    const maxBytes = (details as { maxBytes?: unknown }).maxBytes;
    if (typeof maxBytes !== 'number' || !Number.isFinite(maxBytes) || maxBytes <= 0) {
      return 'Media file exceeds the upload size limit.';
    }
    return `Media file exceeds the upload size limit (${formatBinaryBytes(maxBytes)} max).`;
  };

  // Matches the 413 shape returned by /api/web/ingest/text when the trimmed
  // body exceeds the pre-ingest character cap. Without this case the inline
  // result card falls through to generic "Something went wrong" copy.
  const resolveTextTooLargeMessage = (details: unknown): string => {
    const fallback = 'Text is too large. Paste a shorter excerpt or split the file.';
    if (!details || typeof details !== 'object') {
      return fallback;
    }
    const maxBytes = (details as { maxBytes?: unknown }).maxBytes;
    if (typeof maxBytes !== 'number' || !Number.isFinite(maxBytes) || maxBytes <= 0) {
      return fallback;
    }
    return `Text is too large (${formatBinaryBytes(maxBytes)} max). Paste a shorter excerpt or split the file.`;
  };

  if (error instanceof WebTryError) {
    switch (error.message) {
      case 'auth_not_configured':
        return 'Login is temporarily unavailable. Please try again later.';
      case 'auth_required':
        return 'Please log in or install the extension to continue.';
      case 'payment_required':
        return 'Membership required. Become a member to keep gisting.';
      case 'entitlement_unavailable':
        return 'Membership check is temporarily unavailable. Please try again.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/operation-not-allowed':
        return 'Anonymous login is disabled. Please log in or contact support.';
      case 'auth/unauthorized-domain':
        return 'This domain is not authorized for login. Please use gist.is or contact support.';
      case 'auth/invalid-api-key':
      case 'auth/configuration-not-found':
        return 'Login is misconfigured. Please contact support.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Invalid email or password.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection.';
      case 'auth/popup-closed-by-user':
        return 'Login cancelled.';
      case 'auth/cancelled-popup-request':
        return 'Login cancelled.';
      case 'google_sign_in_failed':
        return 'Login failed. Please try again.';
      case 'email_sign_in_failed':
        return 'Login failed. Please try again.';
      case 'invalid_media_type':
        return 'Unsupported file type. Upload audio or video files.';
      case 'media_duration_too_long':
        return 'File is too long. Audio/video uploads must be 9.5 hours or less.';
      case 'media_too_large':
        return resolveMediaTooLargeMessage(error.details);
      case 'Media file exceeds size limit':
      case 'Video exceeds size limit':
        return 'Media file exceeds the upload size limit.';
      case 'invalid_pdf_type':
        return 'Only PDF files are supported for PDF uploads.';
      case 'markdown_too_large':
        return 'File is too large. Please try a smaller file.';
      case 'text_too_large':
        return resolveTextTooLargeMessage(error.details);
      case 'text_required':
        return 'Please paste some text to continue.';
      case 'upload_cors_blocked':
        return 'Upload blocked by storage CORS. Allow https://next.gist.is to PUT to your GCS bucket.';
      case 'upload_timeout':
        return 'Upload timed out. Please check your connection and try again.';
      case 'ws_token_missing':
        return 'Streaming is temporarily unavailable. Waiting for the full summary instead.';
      case 'ws_url_invalid':
        return 'Streaming is temporarily unavailable. Waiting for the full summary instead.';
      case 'share_not_ready':
        return 'Finalizing your upload. Please wait a few seconds and try again.';
      case 'share_create_timeout':
        return 'Creating your share link took too long. Please try again.';
      case 'share_create_network':
        return 'Network issue while creating your share link. Please try again.';
      case 'share_create_failed':
        return 'Could not create a share link. Please try again.';
      case 'web_scraper_not_configured':
      case 'web_scraper_request_failed':
      case 'web_scraper_invalid_completed_response':
      case 'commit_failed_http':
      case 'commit_missing_content_id':
        return 'Web ingest is temporarily unavailable. Please try again.';
      case 'unsupported_host':
        return 'This URL is blocked for safety. Use a public http(s) URL.';
      case 'reader_protocol_load_failed':
      case 'reader_bundle_load_failed':
      case 'reader_bundle_missing':
        return 'Reader failed to load. Please refresh and try again.';
      case 'reader_dom_missing':
        return 'Reader UI is misconfigured. Please refresh and try again.';
      case 'upload_failed':
        return 'Upload failed. Please check your connection and try again.';
      case 'upload_network_error':
        return 'Upload failed due to a network error. Please check your connection and try again.';
      case 'poll_timeout':
        return 'Processing is taking longer than expected. Please try again.';
      case 'content_failed':
        return 'Content processing failed. Please try again.';
      case 'media_upload_missing':
        return 'Upload not found. Please try uploading again.';
      case 'media_status_invalid':
        return 'Could not check upload status. Please try again.';
      case 'media_status_failed':
        return 'Upload processing failed. Please try again.';
      case 'media_extraction_failed':
        return 'Audio extraction failed. Please try a different file.';
      case 'media_upload_url_missing':
        return 'Upload initialization failed. Please try again.';
      case 'media_transcode_aborted':
        return 'Audio processing was interrupted. Please try again.';
      case 'media_transcode_timeout':
        return 'Audio processing took too long. Please try a smaller file.';
      case 'media_transcode_unsupported_api':
        return 'Your browser cannot process audio here. Try a different browser or upload a different file.';
      case 'media_transcode_unsupported_media_type':
        return 'Unsupported audio or video format. Please try a different file.';
      case 'media_transcode_unsupported_codec':
        return 'This audio codec is not supported. Please try a different file.';
      case 'media_transcode_input_too_large':
      case 'media_transcode_output_too_large':
        return 'Audio file is too large to process in the browser. Please try a shorter file.';
      case 'media_transcode_decode_failed':
      case 'media_transcode_demux_failed':
        return 'Could not read this audio or video file. Please try a different file.';
      case 'url_invalid':
        return 'That does not look like a valid http(s) URL.';
      case 'poll_check_timeout':
        return 'Server response took too long. Please try again.';
      case 'content_id_missing':
      case 'share_url_missing':
        return 'Server response was incomplete. Please try again.';
      case 'ws_content_id_required':
        return 'Streaming is temporarily unavailable. Waiting for the full summary instead.';
      case 'url_required':
        return 'Please paste a URL to continue.';
      case 'unauthorized':
        return 'Please log in to continue.';
      case 'commit_failed':
        return 'Saving your gist failed. Please try again.';
      case 'cloudflare_not_configured':
      case 'cloudflare_auth_error':
        return 'Web ingest is temporarily unavailable. Please try again.';
      case 'invalid_content_length':
        return 'File is too large or invalid. Please try a different file.';
      case 'upload_forbidden':
        return 'Please log in again to upload.';
      case 'signed_url_failed':
      case 'kv_write_failed':
      case 'bucket_not_configured':
        return 'Upload service is temporarily unavailable. Please try again.';
      case 'request_failed':
        return 'Request failed. Please check your connection and try again.';
      case 'request_timeout':
        return 'Starting your Gist took too long. Please try again.';
      case 'request_network':
        return 'Network issue while starting your Gist. Please check your connection and try again.';
      case 'empty_response':
        return 'No response received. Please try again.';
      default:
        return 'Something went wrong. Please try again.';
    }
  }

  if (isAbortLikeError(error)) {
    return 'Starting your Gist took too long. Please try again.';
  }

  return 'Something went wrong. Please try again.';
}

/**
 * Formats byte values into human-readable binary units.
 */
function formatBinaryBytes(bytes: number): string {
  const units = ['B', 'KiB', 'MiB', 'GiB'] as const;
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const rounded = value >= 100 || unitIndex === 0
    ? Math.round(value)
    : Math.round(value * 10) / 10;
  return `${rounded} ${units[unitIndex]}`;
}

function isAbortLikeError(error: unknown): boolean {
  const name = error && typeof error === 'object' && 'name' in error
    ? (error as { name?: unknown }).name
    : null;
  return name === 'AbortError' || name === 'TimeoutError';
}

/**
 * Extracts a Firebase auth error code string.
 */
export function extractFirebaseAuthErrorCode(error: unknown): string | null {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: unknown }).code;
    return typeof code === 'string' ? code : null;
  }
  return null;
}
